/**
 * ReservationsPanel — the Reservations tab (BON-382, BON-472).
 *
 * CSR-created reservations only. A customer part-way through their own checkout
 * is not shown here: those live ten minutes, they would churn under the reader,
 * and none of the actions apply to them — there is no emailed link to resend and
 * no credential we could recover to build one.
 *
 * Three actions per row: Resend link, Edit and Release. All three are gated on
 * booking-write permission HERE rather than per row, because `usePermissions`
 * decodes a token on every call and a table of rows would each do it again.
 */
import { Search } from 'lucide-react';

import { usePermissions } from '../../hooks/usePermissions';
import { Permission } from '../../types/permissions';

import { ReservationActionDialogs } from './ReservationActionDialogs';
import { ReservationsHistoryToggle } from './ReservationsHistoryToggle';
import { ReservationsListBody } from './ReservationsListBody';
import { useReservationRowActions } from './useReservationRowActions';
import type { ReservationsListState } from './useReservationsList';

export function ReservationsPanel({
  s,
  actorName,
}: {
  s: ReservationsListState;
  actorName: string;
}) {
  const { hasAnyPermission } = usePermissions();
  // Same reasoning as the New booking button: the API refuses these anyway, so
  // drawing them for a role that lacks write:bookings would be a live-looking
  // control that can only 403.
  const canWrite = hasAnyPermission(Permission.WRITE_BOOKINGS, Permission.WRITE_ALL);
  const actions = useReservationRowActions(s.refresh);

  return (
    <div>
      <div className="mb-4 rounded-xl bg-stone-100 px-4 py-3 text-sm text-stone-600 dark:bg-[#1e2520] dark:text-stone-300">
        Reservations are unpaid seat holds a CSR created for a customer. They hold capacity until
        the timer runs out, at which point the seats go back on sale on their own. One becomes a
        booking — and moves to the Bookings tab — only once payment clears.
      </div>

      <div className="mb-4 flex items-center justify-between gap-4">
        <div className="relative max-w-md flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <input
            type="search"
            value={s.search}
            onChange={(e) => s.setSearch(e.target.value)}
            placeholder="Search name, email, hold…"
            aria-label="Search reservations"
            className="w-full rounded-lg border border-stone-300 py-2 pl-9 pr-3 text-sm dark:border-stone-600 dark:bg-[#1e2520] dark:text-stone-100"
          />
        </div>
        <div className="flex flex-shrink-0 items-center gap-4">
          <ReservationsHistoryToggle checked={s.includeInactive} onChange={s.setIncludeInactive} />
          {/* Seats actually held right now, counted server-side from the holds
              table. Unaffected by the toggle beside it: every row it reveals is
              finished and holds nothing. */}
          <p className="text-sm text-stone-500 dark:text-stone-400">{s.seatsHeld} seats held</p>
        </div>
      </div>

      <ReservationsListBody
        s={s}
        canWrite={canWrite}
        onResend={(r) => actions.open('resend', r)}
        onEdit={(r) => actions.open('edit', r)}
        onRelease={(r) => actions.open('release', r)}
      />

      <ReservationActionDialogs actions={actions} actorName={actorName} />
    </div>
  );
}
